/**
 * The rules of Recent Bids on the player's Auction Details page (#239, PL-019.4, mockup v7 States
 * 4, 5, 7 and 12): the five newest bids of the selected team, read from Player/GetLotRecentBids, with
 * the bidder, tags, how long ago, and the amount. Pure; the page's hook is Player/useRecentBids.js.
 */
import { formatStart } from './auctionList';
import { TEAM_STATES, teamState } from './auctionDetails';
import { parseServerUtc } from './lotClock';

/** How many bids the block shows, newest first, with no "View all" (#239 AC2). */
export const RECENT_BIDS_LIMIT = 5;

/**
 * Reads Player/GetLotRecentBids. A refusal (the auction or team is not the player's to see) reads
 * as no bids; no reply at all is null, so the page keeps what it shows.
 *
 * @param {*} res The auth-service wrapper's answer.
 * @returns {{teamId: *, serverNowUtc: (string|null), bids: object[]}|null}
 */
export const readRecentBidsReply = (res) => {
    if (res?.status !== 200 || !res.data || typeof res.data !== 'object') return null;
    const data = res.data;
    return {
        teamId: data.teamId ?? null,
        serverNowUtc: data.serverNowUtc ?? null,
        bids: data.status === true && Array.isArray(data.bids) ? data.bids.slice(0, RECENT_BIDS_LIMIT) : [],
    };
};

const firstLetter = (text) => (typeof text === 'string' ? text.trim().charAt(0) : '');

/**
 * The bidder's first and last name, as the platform shows names elsewhere (#239 AC3).
 *
 * @param {object} bid A bid of the reply.
 * @returns {string}
 */
export const bidderName = (bid) =>
    [bid?.bidderFirstName, bid?.bidderLastName]
        .filter((part) => typeof part === 'string' && part.trim())
        .map((part) => part.trim())
        .join(' ');

/**
 * What a bidder without a profile photo shows instead (#239 AC4): the capital first letters of
 * their first and last name, "DP" for David Park.
 *
 * @param {object} bid A bid of the reply.
 * @returns {string}
 */
export const bidderInitials = (bid) =>
    `${firstLetter(bid?.bidderFirstName)}${firstLetter(bid?.bidderLastName)}`.toUpperCase();

/**
 * How long ago a bid was placed, in whole seconds, on the server's clock: its age when the reply was
 * read, plus the time since the reply arrived, so a device clock that is off does not matter (#191).
 *
 * @param {object} bid A bid of the reply (bidDateTime, UTC).
 * @param {{serverNowUtc: (string|null), receivedAt: number}} reply When the server read it, and when it arrived.
 * @param {number} now Date.now().
 * @returns {number|null} Seconds, or null when the bid has no usable time.
 */
export const bidAgeSeconds = (bid, { serverNowUtc, receivedAt }, now) => {
    const placed = parseServerUtc(bid?.bidDateTime);
    if (!Number.isFinite(placed)) return null;
    const serverNow = parseServerUtc(serverNowUtc);
    const ageMs = Number.isFinite(serverNow) && Number.isFinite(receivedAt)
        ? serverNow - placed + (now - receivedAt)
        : now - placed;
    return Math.max(0, Math.floor(ageMs / 1000));
};

/**
 * The time-ago line (#239 AC8): "Just now" under 5 seconds, then seconds, minutes and hours ago,
 * and from 24 hours the bid's date and time (MM/DD/YYYY hh:mm AM/PM, in the player's time, as every
 * date in the app).
 *
 * @param {number|null} seconds bidAgeSeconds' answer.
 * @param {string} bidDateTime The bid's time, for the 24-hour case.
 * @returns {string}
 */
export const timeAgoText = (seconds, bidDateTime) => {
    if (seconds === null || seconds === undefined) return formatStart(bidDateTime);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    }
    if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    return formatStart(bidDateTime);
};

/** The tags after a bidder's name (#239 AC5-AC7). */
export const BID_TAGS = {
    YOU: { key: 'you', label: 'You' },
    HIGHEST: { key: 'top', label: 'Highest' },
    WINNER: { key: 'top', label: 'Winner' },
};

/**
 * A row's tags: "You" on the player's own bids; on the lot's top bid, "Highest" while the team is in
 * Bidding now and "Winner" once it is sold. A row can carry both. The top bid is the server's
 * (isTopBid, the settlement tie-break), which is the newest bid in every lot since #145.
 *
 * @param {object} bid A bid of the reply.
 * @param {object} lot The selected lot.
 * @returns {Array<{key: string, label: string}>}
 */
export const bidTags = (bid, lot) => {
    const tags = [];
    if (bid?.isYou) tags.push(BID_TAGS.YOU);
    if (bid?.isTopBid) {
        const state = teamState(lot);
        if (state === TEAM_STATES.LIVE) tags.push(BID_TAGS.HIGHEST);
        else if (state === TEAM_STATES.SOLD) tags.push(BID_TAGS.WINNER);
    }
    return tags;
};
